class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return parseFloat(this.dataset.delay || 0);
  }

  async init() {
    this.insertCursor();
    await this.start(this.dataset.firstText, this.startDelay);

    setTimeout(async () => {
      await this.reset();
      await this.start(this.dataset.lastText, 0);
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;
    await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');
    await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';
    this.parentElement.insertBefore(this.cursor, this.nextSibling);
  }
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      const url = this.buildUrl().toString();
      this.renderSection(url, event);
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.latestUrl = null;
  }

  getQuery() {
    return this.input.value.trim();
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.dataset.limit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    const url = this.buildUrl().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.latestUrl = url;
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.removeAttribute('loading');
    this.setAttribute('results', '');
  }

  async renderSectionFromFetch(url) {
    this.setAttribute('loading', '');
    const recipeUrl = new URL(url);
    recipeUrl.searchParams.set('resources[type]', 'product');
    recipeUrl.searchParams.set('resources[options][unavailable_products]', 'show');
    const read = async (requestUrl) => {
      const response = await fetch(requestUrl);
      if (!response.ok) throw new Error('Search request failed');
      return response.text();
    };
    try {
      const [primary, recipes] = await Promise.all([
        read(url),
        read(recipeUrl.toString()).catch(() => null)
      ]);
      let responseText = primary;
      if (recipes) {
        const parser = new DOMParser();
        const primaryDocument = parser.parseFromString(primary, 'text/html');
        const recipeDocument = parser.parseFromString(recipes, 'text/html');
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const primaryResults = primaryDocument.getElementById(id);
        const recipeResults = recipeDocument.getElementById(id);
        const target = primaryResults?.querySelector('[data-predictive-recipes]');
        const source = recipeResults?.querySelector('[data-predictive-recipes]');
        if (target && source) {
          source.hidden = !source.querySelector('a');
          target.replaceWith(source.cloneNode(true));
          if (source.querySelector('a')) {
            primaryResults.querySelector('.search__empty')?.remove();
            if (!primaryResults.querySelector('.drawer__sticky')) {
              const more = recipeResults.querySelector('.drawer__sticky');
              if (more) primaryResults.appendChild(more.cloneNode(true));
            }
          }
          responseText = primaryDocument.documentElement.outerHTML;
        }
      }
      if (recipes) this.cachedMap.set(url, responseText);
      if (this.latestUrl !== url) return;
      this.renderSearchResults(responseText);
      this.setAttribute('results', '');
    } catch (_) {
      if (this.latestUrl === url) this.removeAttribute('results');
    } finally {
      if (this.latestUrl === url) this.removeAttribute('loading');
    }
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
    if (document.getElementById(id) === null) return;

    const results = document.getElementById(id);
    results.innerHTML = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(id).innerHTML;
    if (!this.closest('search-drawer')) return;
    try {
      if (!this.intentClassifier && window.AnydaySearchIntent) {
        const data = this.querySelector('[data-predictive-intent-data]');
        this.intentClassifier = window.AnydaySearchIntent.compile(
          JSON.parse(data.querySelector('[data-food-index]').textContent),
          JSON.parse(data.querySelector('[data-intent-overrides]').textContent)
        );
      }
      if (this.intentClassifier?.(this.getQuery()).tab !== 'recipes') return;
      const products = results.querySelector('[data-predictive-products]');
      const recipes = results.querySelector('[data-predictive-recipes]');
      if (products && recipes && recipes.querySelector('a')) products.before(recipes);
    } catch (_) {}
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
